import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificandoToken, setVerificandoToken] = useState(true);
  const [tokenValido, setTokenValido] = useState(false);
  const [mensagem, setMensagem] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    verificarToken();
  }, [token]);

  const verificarToken = async () => {
    if (!token) {
      setMensagem({ tipo: 'error', texto: 'Token de reset não encontrado na URL.' });
      setVerificandoToken(false);
      return;
    }

    try {
      console.log('[ResetPassword] Verificando token...');
      const response = await api.get(`/auth/verify-reset-token/${token}`);

      if (response.data.sucesso) {
        setTokenValido(true);
        console.log('[ResetPassword] Token válido');
      } else {
        setMensagem({ 
          tipo: 'error', 
          texto: response.data.mensagem || 'Token inválido ou expirado.' 
        });
      }
    } catch (error: any) {
      console.error('[ResetPassword] Erro ao verificar token:', error);
      setMensagem({ 
        tipo: 'error', 
        texto: 'Token inválido ou expirado. Solicite um novo link de recuperação.' 
      });
    } finally {
      setVerificandoToken(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!novaSenha || !confirmarSenha) {
      setMensagem({ tipo: 'error', texto: 'Por favor, preencha todos os campos.' });
      return;
    }

    if (novaSenha.length < 6) {
      setMensagem({ tipo: 'error', texto: 'A senha deve ter no mínimo 6 caracteres.' });
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setMensagem({ tipo: 'error', texto: 'As senhas não coincidem.' });
      return;
    }

    setLoading(true);
    setMensagem(null);

    try {
      console.log('[ResetPassword] Redefinindo senha...');
      
      const response = await api.post('/auth/reset-password', {
        token,
        novaSenha
      });

      if (response.data.sucesso) {
        setMensagem({ 
          tipo: 'success', 
          texto: response.data.mensagem || 'Senha redefinida com sucesso!' 
        });
        
        // Redirecionar para login após 3 segundos
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setMensagem({ 
          tipo: 'error', 
          texto: response.data.mensagem || 'Erro ao redefinir senha.' 
        });
      }
    } catch (error: any) {
      console.error('[ResetPassword] Erro ao redefinir senha:', error);
      setMensagem({ 
        tipo: 'error', 
        texto: error.response?.data?.mensagem || 'Erro ao redefinir senha. Tente novamente.' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (verificandoToken) {
    return (
      <div className="login-page">
        <div className="login-box">
          <div className="card card-outline card-primary">
            <div className="card-body text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Verificando token...</span>
              </div>
              <p className="mt-3">Verificando token de recuperação...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValido) {
    return (
      <div className="login-page">
        <div className="login-box">
          <div className="card card-outline card-danger">
            <div className="card-header text-center">
              <h1 className="mb-0">
                <b>Sistema</b> Sophya
              </h1>
            </div>
            <div className="card-body">
              {mensagem && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {mensagem.texto}
                </div>
              )}
              
              <div className="text-center mt-3">
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/forgot-password')}
                >
                  Solicitar Novo Link
                </button>
                <button 
                  className="btn btn-link"
                  onClick={() => navigate('/login')}
                >
                  Voltar para o Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="card card-outline card-primary">
          <div className="card-header text-center">
            <h1 className="mb-0">
              <b>Sistema</b> Sophya
            </h1>
          </div>
          <div className="card-body">
            <p className="login-box-msg">Digite sua nova senha</p>

            {mensagem && (
              <div className={`alert alert-${mensagem.tipo === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
                <button type="button" className="btn-close" onClick={() => setMensagem(null)}></button>
                <i className={`bi bi-${mensagem.tipo === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
                {mensagem.texto}
              </div>
            )}

            {mensagem?.tipo !== 'success' && (
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <div className="input-group">
                    <input
                      type={mostrarSenha ? 'text' : 'password'}
                      className="form-control"
                      placeholder="Nova senha (mínimo 6 caracteres)"
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      disabled={loading}
                      required
                      minLength={6}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setMostrarSenha(!mostrarSenha)}
                      disabled={loading}
                    >
                      <i className={`bi bi-eye${mostrarSenha ? '-slash' : ''}`}></i>
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="input-group">
                    <input
                      type={mostrarSenha ? 'text' : 'password'}
                      className="form-control"
                      placeholder="Confirmar nova senha"
                      value={confirmarSenha}
                      onChange={(e) => setConfirmarSenha(e.target.value)}
                      disabled={loading}
                      required
                      minLength={6}
                    />
                    <div className="input-group-text">
                      <i className="bi bi-lock"></i>
                    </div>
                  </div>
                </div>

                {novaSenha && confirmarSenha && novaSenha !== confirmarSenha && (
                  <div className="alert alert-warning alert-sm">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    As senhas não coincidem
                  </div>
                )}

                <div className="row">
                  <div className="col-12">
                    <button 
                      type="submit" 
                      className="btn btn-primary w-100"
                      disabled={loading || novaSenha !== confirmarSenha}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Redefinindo...
                        </>
                      ) : (
                        'Redefinir Senha'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}

            <div className="mt-3 text-center">
              <button 
                className="btn btn-link"
                onClick={() => navigate('/login')}
                disabled={loading}
              >
                Voltar para o Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
